import React, {useCallback, useEffect, useState} from 'react';
import clsx from 'clsx';
import 'core-css/ui/iconDefaults.css';
import {LinearProgress, Typography} from '@mui/material';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	ASSET_IMAGE_WORKING_CLASS_NAME,
	assetImageWorkingClasses,
	AssetImageWorkingClasses,
	getAssetImageWorkingUtilityClass,
} from './assetImageWorkingClasses';

export type AssetImageWorkingElement = HTMLDivElement;
export type AssetImageWorkingRef = React.Ref<AssetImageWorkingElement>;

type AssetImageWorkingUtilityClassProps = WithClasses<
	AssetImageWorkingClasses,
	{}
>;

const useUtilityClasses = (ownerState: AssetImageWorkingUtilityClassProps) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		progress: ['progress'],
		primary: ['primary'],
	};
	return composeClasses(slots, getAssetImageWorkingUtilityClass, classes);
};

const AssetImageWorkingRoot = styled('div', {
	name: ASSET_IMAGE_WORKING_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	background: theme.palette.custom.background.backdrop,
	alignItems: 'center',
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: 0,
	position: 'relative',
	userSelect: 'none',
}));

const AssetImageWorkingProgress = styled(LinearProgress, {
	name: ASSET_IMAGE_WORKING_CLASS_NAME,
	slot: 'Progress',
})(({theme}) => ({
	width: `calc(100% - ${theme.spacing(4)})`,
}));

const AssetImageWorkingPrimary = styled(Typography, {
	name: ASSET_IMAGE_WORKING_CLASS_NAME,
	slot: 'Primary',
})(({theme}) => ({
	marginBottom: theme.spacing(1),
	textAlign: 'center',
}));

export interface AssetImageWorkingProps
	extends React.DetailedHTMLProps<
			React.HTMLAttributes<AssetImageWorkingElement>,
			AssetImageWorkingElement
		>,
		WithClasses<
			AssetImageWorkingClasses,
			{
				className?: string;
				primary?: string;
				progress?: number;
				//ref?: AssetImageWorkingRef;
			}
		> {}

const AssetImageWorkingInner = (
	{
		classes: _classes,
		className,
		progress,
		primary,
		...props
	}: AssetImageWorkingProps,
	ref?: AssetImageWorkingRef,
) => {
	const classes = useUtilityClasses({
		classes: _classes,
	});

	return (
		<AssetImageWorkingRoot
			className={clsx(classes.root, className)}
			{...props}
			ref={ref}
		>
			{primary != null ? (
				<AssetImageWorkingPrimary className={classes.primary} variant="caption">
					{primary}
				</AssetImageWorkingPrimary>
			) : null}
			<AssetImageWorkingProgress
				className={classes.progress}
				variant={progress != null ? 'determinate' : 'indeterminate'}
				value={progress != null ? progress : undefined}
			/>
		</AssetImageWorkingRoot>
	);
};

export const AssetImageWorkingPrimitive = React.forwardRef(
	AssetImageWorkingInner,
) as (props: AssetImageWorkingProps, ref?: AssetImageWorkingRef) => JSX.Element;

export const AssetImageWorking = React.memo(AssetImageWorkingPrimitive) as (
	props: AssetImageWorkingProps,
	ref?: AssetImageWorkingRef,
) => JSX.Element;

export default AssetImageWorking;
